/**
 * Ablage des Zugangstokens.
 *
 * **Im Arbeitsspeicher, nicht im localStorage.** docs/04, Abschnitt 4 sagt
 * das ausdruecklich fuer das Erneuerungstoken; fuer das Zugangstoken gilt
 * derselbe Grund: Alles im localStorage ist fuer jedes Skript auf der Seite
 * lesbar. Eine einzige Skriptluecke -- in einer Abhaengigkeit, in einer
 * eingebetteten Ansicht -- genuegt dann, um eine Sitzung abzugreifen.
 *
 * Die Kehrseite ist bekannt und in Kauf genommen: Nach einem Neuladen der
 * Seite ist das Zugangstoken fort. Die Sitzung bleibt trotzdem bestehen, weil
 * das Erneuerungstoken im `httpOnly`-Cookie liegt und beim Start einmal gegen
 * ein neues Zugangstoken getauscht wird. Der Nutzer merkt davon nichts ausser
 * einem kurzen Ladezustand.
 */

export interface SessionState {
  accessToken: string;
  expiresAt: Date;
  tenantId: string | null;
  requiresTenantSelection: boolean;
}

type Listener = (session: SessionState | null) => void;

let current: SessionState | null = null;
const listeners = new Set<Listener>();

export function getSession(): SessionState | null {
  return current;
}

export function getAccessToken(): string | null {
  return current?.accessToken ?? null;
}

export function setSession(session: SessionState | null): void {
  current = session;
  for (const listener of listeners) listener(session);
}

export function clearSession(): void {
  setSession(null);
}

export function subscribeToSession(listener: Listener): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

/**
 * Meldet, ob das Token demnaechst ablaeuft.
 *
 * Die Reserve von einer Minute ist kein Zufallswert: Sie deckt die Laufzeit
 * einer Anfrage und eine ungenau gehende Uhr im Browser ab. Ohne sie liefe ein
 * Aufruf gelegentlich mit einem Token los, das beim Eintreffen am Server
 * gerade abgelaufen ist -- und der Nutzer saehe eine Fehlermeldung, fuer die
 * es keinen Anlass gab.
 */
export function isExpiringSoon(session: SessionState, reserveSeconds = 60): boolean {
  return session.expiresAt.getTime() - Date.now() < reserveSeconds * 1000;
}
